import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { TabBar, Button, Filter } from '@chikara/ui';
import { ActivateCardModal } from '@/features/card/components/ActivateCardModal';
import { PageContent } from '@/shared/components/PageContent';
import { CardAssignedTable } from '@/features/card/components/CardAssignedTable/CardAssignedTable';
import { CardBatchesView } from '@/features/card/components/CardBatchesView/CardBatchesView';
import { useCards } from '@/features/card/hooks/useCard/useCards';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import styles from './CardPage.module.css';
import { t } from '@/i18n';


export default function CardPage() {
  const { userData } = useAuth();
  const basic = isBasic(userData.role);
  const [activeTab, setActiveTab] = useState('asigned');
  const [selectedStatus, setSelectedStatus] = useState<string>('enabled');
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);

  const location = useLocation();
  const tab = location.state?.tab;

  const { cards, pagination, refetch } = useCards();

  const isPendingStatus = (status: string) => {
    const s = status?.toString().toLowerCase();
    return s === 'pending_approval' || s === 'pending';
  };

  const tabs = [
    {
      label: t('dashboard.card.tabs.assigned'),
      value: 'asigned',
    },
    ...(!basic ? [
      {
        label: activeTab === 'pending' && cards.length > 0
          ? `${t('dashboard.card.tabs.pending')} (${cards.length })`
          : t('dashboard.card.tabs.pending'),
        value: 'pending',
      },
      {
        label: t('dashboard.card.tabs.tracking'),
        value: 'traking',
      },
    ] : [])
  ];

  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
  }, [tab]);

  useEffect(() => {
    if (basic) {
      refetch(1, 10, undefined);
      return;
    }

    if (activeTab === 'pending') {
      refetch(1, 10, 'pending_approval');
    } else if (activeTab === 'asigned') {
      refetch(1, 10, selectedStatus || undefined);
    }
  }, [activeTab, refetch, selectedStatus, basic]);

  const displayedCards = basic
    ? cards
    : cards.filter(card => {
      if (activeTab === 'pending') return true;
      if (activeTab === 'asigned') return !isPendingStatus(card.status);
      return true;
    });

  return (
    <PageContent 
      title={t('dashboard.card.title')}
      button={!basic && activeTab === 'asigned' ? ( 
        <Filter 
          label='Estatus'
          options={[
            { label: t('dashboard.card.status.enabled'), value: 'enabled' },
            { label: t('dashboard.card.status.disabled'), value: 'disabled' },
            { label: t('dashboard.card.status.blocked'), value: 'blocked' },
          ]}
          value={selectedStatus} 
          onChange={setSelectedStatus} 
        />
      ) : undefined}
    >
      {!basic && (
        <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      )}

      {(activeTab === 'asigned' || activeTab === 'pending') && (
        <div className={styles.content}>
          {cards.length > 0 && activeTab === 'asigned' && (
            <div className={styles.buttonContainer}>
              <Button
                variant="outline"
                size="md"
                onClick={() => setIsActivateModalOpen(true)}
              >
                {t('dashboard.card.actions.activate')}
              </Button>
            </div>
          )}

          <CardAssignedTable
            data={displayedCards}
            onActivateCard={() => setIsActivateModalOpen(true)}
            onRefetch={refetch}
            pagination={pagination}
            basic={basic}
          />
        </div>
      )}

      {activeTab === 'traking' && (
        <div className={styles.content}>
          <CardBatchesView />
        </div>
      )}

      <ActivateCardModal
        isOpen={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        onSuccess={() => {
          refetch(1, 10, undefined);
          setIsActivateModalOpen(false);
        }}
      />
    </PageContent>
  );
}

export { CardPage };
